import {
    useEffect, useState, useRef, forwardRef, useImperativeHandle,
} from 'react';
import {
    Button, Space, Modal, Row, Col, FormInstance,
    Alert,
} from 'antd';
import { useTranslation } from 'react-i18next';
import produce from 'immer';
import { useOrder } from '../../hooks/useOrder';
import Table from '../table';
import OrderItemForm, { OrderItemFormRef, OrderItemFormValues } from './item-form';
import { useFormWatchMode } from '../../hooks/useFormWatchMode';
import EditIcon from '../../assets/icons/edit';
import TrashIcon from '../../assets/icons/trash';
import { useFetch } from '../../hooks/useFetch';

export interface IOrderItemsRef {
    getItems: () => OrderItemFormValues[];
}

interface IOrderItemsProps {
    onItemsChange: (items: OrderItemFormValues[]) => void;
    orderForm: FormInstance;
}

const OrderItems = forwardRef(({ onItemsChange, orderForm }: IOrderItemsProps, ref) => {
    const fetch = useFetch();
    const order = useOrder();
    const { i18n, t } = useTranslation();
    const [dataSource, setDataSource] = useState<OrderItemFormValues[]>([]);
    const [dutyMessage, setDutyMessage] = useState<string>();
    const form = useRef<OrderItemFormRef>();
    const { watchMode } = useFormWatchMode();

    const sum = dataSource.reduce((val, row) => {
        val += row.cost;
        return val;
    }, 0);

    const actions = (_: any, row: OrderItemFormValues) => (
        <Space>
            <Button icon={<EditIcon />} type="link" onClick={() => onEditClick(row)} />
            <Button icon={<TrashIcon />} type="link" onClick={() => onDeleteClick(row)} />
        </Space>
    );

    const getIndex = (row: OrderItemFormValues) => {
        if (typeof row.id === 'number') {
            return dataSource.findIndex((el) => el.id === row.id);
        }
        if (typeof row.newRowId === 'number') {
            return dataSource.findIndex((el) => el.newRowId === row.newRowId);
        }
        return -1;
    };

    const onEditClick = (row: OrderItemFormValues) => {
        if (form.current) {
            const index = getIndex(row);
            if (index > -1) {
                form.current.show(row, index);
            }
        }
    };

    const onDeleteClick = (row: OrderItemFormValues) => {
        Modal.confirm({
            title: t('confirmAction'),
            content: t('deleteConfirmation'),
            onOk: () => {
                const index = getIndex(row);
                if (index > -1) {
                    const nextState = produce(dataSource, (draft) => {
                        draft?.splice(index, 1);
                    });
                    setDataSource(nextState);
                    if (typeof onItemsChange === 'function') {
                        onItemsChange(nextState);
                    }
                }
            },
            okText: t('delete'),
        });
    };

    const onFormSubmit = (values: OrderItemFormValues) => {
        const { index } = values;
        let nextState: OrderItemFormValues[] = dataSource;
        if (typeof index === 'undefined') {
            nextState = produce(dataSource, (draft) => {
                draft.unshift(values);
            });
        } else if (typeof index !== 'undefined') {
            nextState = produce(dataSource, (draft) => {
                if (draft[index]) {
                    draft[index] = values;
                }
            });
        }
        setDataSource(nextState);
        if (typeof onItemsChange === 'function') {
            onItemsChange(nextState);
        }
    };

    const onPlusClick = () => {
        if (form.current) {
            form.current.show();
        }
    };

    const validateDutySum = async () => {
        if (sum > 0) {
            const response = await fetch.request({
                url: '/orders/validate-duty-sum',
                method: 'post',
                data: {
                    from: orderForm.getFieldValue('from'),
                    sum,
                },
            });
            setDutyMessage(response.success ? response.message : undefined);
        } else {
            setDutyMessage(undefined);
        }
    };

    useEffect(() => {
        setDataSource(order.data?.package?.items || []);
    }, [order.data]);

    useEffect(() => {
        validateDutySum();
    }, [i18n.language, sum]);

    useImperativeHandle(ref, () => ({
        getItems: () => dataSource || [],
    }));

    const totalSum = () => (
        <span className="order-items-sum">{`${t('sum')}: ${sum}€`}</span>
    );

    return (
        <Row gutter={[30, 30]}>
            <OrderItemForm ref={form} orderForm={orderForm} onSubmit={onFormSubmit} />
            {!watchMode && (
                <Col xs={24}>
                    <Button type="primary" onClick={onPlusClick}>
                        {t('fillOrderPackages')}
                    </Button>
                </Col>
            )}
            <Col xs={24}>
                <Table
                    scroll={{ x: true }}
                    dataSource={dataSource}
                    bordered
                    columns={[{
                        align: 'center',
                        dataIndex: 'name',
                        title: t('orderPackageName'),
                    }, {
                        align: 'center',
                        dataIndex: 'count',
                        title: t('orderPackageItemsCount'),
                    }, {
                        align: 'center',
                        dataIndex: 'cost',
                        title: t('orderPackageCost'),
                    }, ...(watchMode ? [] : [{
                        dataIndex: '',
                        render: actions,
                        width: 100,
                    }])]}
                    pagination={{
                        className: 'order-items-pagination',
                        pageSize: dataSource.length,
                        showTotal: sum > 0 ? totalSum : undefined,
                    }}
                />
                {typeof dutyMessage === 'string' && (
                    <Alert className="order-items-sum-alert" message={dutyMessage} />
                )}
            </Col>
        </Row>
    );
});

export default OrderItems;
